import React from 'react';
import PropTypes from 'prop-types';
import { LoadingIndicator } from '@spotify-internal/encore-web';
import Overlay from 'shared/Overlay';
import Image from 'shared/Image';
import ColorPicker from 'shared/ColorPicker';
import Box from 'shared/Box';
import Text from 'shared/Text';
import Icon from 'shared/Icon';
import If from 'shared/If';
import Mask from 'shared/Mask';
import DropdownButton from 'shared/DropdownButton';
import Font from 'shared/Font';
import { Spinner } from 'shared/Spinner';
import { FieldToggle } from 'client/components/FieldToggle';
import TextImage from './components/TextImage';
import SceneHeading from '../SceneHeading';
import { FONTS } from '../../constants';
import styles from './EditTextScene.sass';
import {
  ToggleContainer,
  VerticalAlignmentLabel,
  HorizontalJustificationLabel,
  ColorLabel,
  ColorPickerButton,
  UpdateButton,
  HiddenInput,
  InputContainer,
  VerticalAlignmentContainer,
} from './styles';

const getFontFromFontId = (fontId, fonts) => {
  const fontFromId = fonts.find(font => font.id === fontId);
  return fontFromId || null;
};

const noop = () => null;

export class EditTextScene extends React.PureComponent {
  render() {
    const {
      croppedCoverArtImageUrl,
      onToggleApplyText,
      textImageUrl,
      maybeSelectedFontId,
      textHex,
      isTextShowing,
      onSelectFont,
      onSelectJustification,
      justification,
      onSelectAlignment,
      alignment,
      onSelectColor,
      onSubmitCoverArt,
      isButtonDisabled,
      isTextColorLoading,
      isTextImageLoading,
      isColorPickerShowing,
      isAnchorWatermarkShowing,
      onClickColorPickerHide,
      onClickColorPickerShow,
      setHasAnchorBranding,
    } = this.props;
    return (
      <Box width="100%">
        <SceneHeading title="Add text" />
        <Box display="flex" justifyContent="center" wrap width="100%">
          <Box
            width={300}
            smMarginLeft={20}
            smMarginRight={20}
            mdMarginLeft={40}
            mdMarginRight={40}
            lgMarginLeft={40}
            lgMarginRight={40}
          >
            <Box shape="rounded">
              <Mask shape="rounded">
                <Box position="relative">
                  <Image
                    renderLoadingPlaceholder={() => (
                      <Box width="300" height={300} color="gray" />
                    )}
                    height={300}
                    width={300}
                    imageUrl={croppedCoverArtImageUrl}
                  />
                  <If
                    condition={isTextShowing}
                    ifRender={() => (
                      <Box position="absolute" top left>
                        <If
                          condition={isTextImageLoading || isTextColorLoading}
                          ifRender={() => (
                            <Box
                              width={300}
                              height={300}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              color="rgba(255, 255, 255, .3)"
                            >
                              <LoadingIndicator
                                indicatorSize={LoadingIndicator.sm}
                              />
                            </Box>
                          )}
                          elseRender={() => (
                            <TextImage
                              imageUrl={textImageUrl}
                              hex={textHex}
                              width={300}
                              height={300}
                            />
                          )}
                        />
                      </Box>
                    )}
                  />
                  <If
                    condition={isAnchorWatermarkShowing}
                    ifRender={() => (
                      <Box
                        position="absolute"
                        top
                        right
                        marginTop={15}
                        marginRight={16}
                        width={27}
                      >
                        <Icon type="anchor_logo" fillColor="white" />
                      </Box>
                    )}
                  />
                </Box>
              </Mask>
            </Box>
            <ToggleContainer>
              <Text color="#7f8287">Apply Text</Text>
              <FieldToggle value={isTextShowing} onClick={onToggleApplyText} />
            </ToggleContainer>
            <ToggleContainer>
              <Text color="#7f8287">Show Anchor logo on cover art</Text>
              <FieldToggle
                value={isAnchorWatermarkShowing}
                onClick={() => setHasAnchorBranding(!isAnchorWatermarkShowing)}
              />
            </ToggleContainer>
          </Box>
          <Box
            width={300}
            smMarginLeft={20}
            smMarginRight={20}
            mdMarginLeft={40}
            mdMarginRight={40}
            lgMarginLeft={40}
            lgMarginRight={40}
            flex="grow"
            position="relative"
            maxWidth={480}
          >
            <Box width="100%" position="relative">
              <Box marginBottom={28}>
                <Box marginBottom={10}>
                  <Text color="#7f8287">Font</Text>
                </Box>
                <DropdownButton
                  items={FONTS}
                  renderItem={({ id, name }) => (
                    <Box
                      padding={10}
                      color={
                        id === maybeSelectedFontId ? 'gray' : 'transparent'
                      }
                    >
                      <Font fontName={id}>
                        <div style={{ fontSize: 20 }}>{name}</div>
                      </Font>
                    </Box>
                  )}
                  onSelectItem={font => {
                    onSelectFont(font);
                  }}
                  renderToggleButtonContent={() => (
                    <Text>
                      {maybeSelectedFontId ? (
                        getFontFromFontId(maybeSelectedFontId, FONTS) ? (
                          <Font fontName={maybeSelectedFontId}>
                            <div style={{ fontSize: 20 }}>
                              {
                                getFontFromFontId(maybeSelectedFontId, FONTS)
                                  .name
                              }
                            </div>
                          </Font>
                        ) : (
                          'error!' // TODO: This should 'never' happen... but we should do something incase it does
                        ) //
                      ) : (
                        'Choose one...'
                      )}
                    </Text>
                  )}
                  menuHeight="340px"
                />
              </Box>
              <Box marginBottom={28}>
                <Box marginBottom={10}>
                  <Text color="#7f8287">Horizontal Alignment</Text>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  marginTop={10}
                >
                  {[
                    { justificationValue: 'left', label: 'Text left-aligned' },
                    {
                      justificationValue: 'center',
                      label: 'Text center-aligned',
                    },
                    {
                      justificationValue: 'right',
                      label: 'Text right-aligned',
                    },
                  ].map(({ justificationValue, label }) => (
                    <InputContainer key={justificationValue}>
                      <HiddenInput
                        id={`justification-${justificationValue}`}
                        type="radio"
                        name="justification"
                        onClick={() =>
                          onSelectJustification(justificationValue)
                        }
                        checked={justification === justificationValue}
                        aria-label={label}
                      />
                      <HorizontalJustificationLabel
                        htmlFor={`justification-${justificationValue}`}
                      >
                        <Icon type={`justified_${justificationValue}`} />
                      </HorizontalJustificationLabel>
                    </InputContainer>
                  ))}
                </Box>
              </Box>
              <Box marginBottom={28}>
                <Box marginBottom={10}>
                  <Text color="#7f8287">Vertical Alignment</Text>
                </Box>
                <Box
                  marginTop={4}
                  marginBottom={4}
                  display="flex"
                  justifyContent="space-between"
                >
                  {[
                    {
                      text: 'Top',
                      alignmentValue: 'top',
                    },
                    {
                      text: 'Center',
                      alignmentValue: 'center',
                    },
                    {
                      text: 'Bottom',
                      alignmentValue: 'bottom',
                    },
                  ].map(({ text, alignmentValue }) => (
                    <VerticalAlignmentContainer key={alignmentValue}>
                      <HiddenInput
                        id={`alignment-${alignmentValue}`}
                        type="radio"
                        name="alignment"
                        checked={alignment === alignmentValue}
                        onClick={() => onSelectAlignment(alignmentValue)}
                      />
                      <VerticalAlignmentLabel
                        for={`alignment-${alignmentValue}`}
                      >
                        {text}
                      </VerticalAlignmentLabel>
                    </VerticalAlignmentContainer>
                  ))}
                </Box>
              </Box>
              <Box>
                <Box marginBottom={10}>
                  <Text color="#7f8287">Color</Text>
                </Box>
                <Box
                  marginTop={4}
                  marginBottom={4}
                  display="flex"
                  justifyContent="space-between"
                >
                  {[
                    { color: '#292f36', label: 'Black text color' },
                    { color: '#ffffff', label: 'White text color' },
                    { color: '#dfe0e1', label: 'Light grey text color' },
                    { color: '#979797', label: 'Grey text color' },
                    { color: '#5000b9', label: 'Purple text color' },
                    { color: '#2dcfb3', label: 'Teal text color' },
                  ].map(({ color, label }) => (
                    <InputContainer key={color}>
                      <HiddenInput
                        type="radio"
                        name="color"
                        id={`color-${color.substring(1)}`}
                        onClick={() => onSelectColor(color)}
                        checked={textHex === color}
                        aria-label={label}
                      />
                      <ColorLabel
                        color={color}
                        for={`color-${color.substring(1)}`}
                      >
                        {textHex === color && (
                          <Box width={20}>
                            <Icon type="checkmark" />
                          </Box>
                        )}
                      </ColorLabel>
                    </InputContainer>
                  ))}
                  <ColorPickerButton
                    onClick={e => {
                      e.stopPropagation();
                      onClickColorPickerShow();
                    }}
                    aria-label="Open color picker"
                  >
                    <Icon type="eye_dropper" fillColor="#979797" />
                  </ColorPickerButton>
                  {/* Positioned relative to surrounding box */}
                  <Overlay
                    isShowing={isColorPickerShowing}
                    onClickOutside={onClickColorPickerHide}
                    rootClose
                    container={this}
                    renderContent={() => (
                      <div className={styles.colorPickerOverlay}>
                        <ColorPicker
                          hex={textHex}
                          onSelectColor={onSelectColor}
                          disableAlpha
                        />
                      </div>
                    )}
                  />
                </Box>
                <If
                  condition={!isTextShowing}
                  ifRender={() => (
                    <Box
                      position="absolute"
                      color="rgba(255,255,255, .6)"
                      top
                      right
                      left
                      bottom
                    />
                  )}
                />
              </Box>
            </Box>
            <Box marginTop={80} display="flex" justifyContent="flex-end">
              <Box width={300}>
                <UpdateButton
                  isDisabled={isButtonDisabled}
                  onClick={isButtonDisabled ? null : onSubmitCoverArt}
                  color="purple"
                >
                  Update cover art
                  {isButtonDisabled && <Spinner size={20} color="#7f8287" />}
                </UpdateButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

EditTextScene.defaultProps = {
  croppedCoverArtImageUrl: '',
  onToggleApplyText: noop,
  textImageUrl: '',
  textHex: '#ffffff',
  isTextShowing: true,
  onSelectFont: noop,
  onSelectJustification: noop,
  justification: 'center',
  onSelectAlignment: noop,
  alignment: 'top',
  onSelectColor: noop,
  onSubmitCoverArt: noop,
  isButtonDisabled: false,
  isTextImageLoading: false,
  maybeSelectedFontId: null,
  isColorPickerShowing: false,
  isAnchorWatermarkShowing: false,
  onClickColorPickerHide: noop,
  onClickColorPickerShow: noop,
  isTextColorLoading: false,
};

EditTextScene.propTypes = {
  croppedCoverArtImageUrl: PropTypes.string,
  textImageUrl: PropTypes.string,
  textHex: PropTypes.string,
  isTextShowing: PropTypes.bool,
  onSelectFont: PropTypes.func,
  onToggleApplyText: PropTypes.func,
  onSelectJustification: PropTypes.func,
  justification: PropTypes.oneOf(['left', 'center', 'right']),
  onSelectAlignment: PropTypes.func,
  alignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  onSelectColor: PropTypes.func,
  onSubmitCoverArt: PropTypes.func,
  isButtonDisabled: PropTypes.bool,
  isTextImageLoading: PropTypes.bool,
  maybeSelectedFontId: PropTypes.string,
  isColorPickerShowing: PropTypes.bool,
  isAnchorWatermarkShowing: PropTypes.bool,
  onClickColorPickerHide: PropTypes.func,
  onClickColorPickerShow: PropTypes.func,
  isTextColorLoading: PropTypes.bool,
};
